// NodeAddRelatedButton.jsx

import React, { useRef } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// CSS
import './NodeAddRelatedButton.css';

// Icons
import Icon from 'svg/Icon';

// Components
import AddActionMenu from 'components/addmenu/AddActionMenu';
import Button from 'components/button/Button';
import useNodeAddRelated from 'node/addrelated/useNodeAddRelated';

/**
 * NodeAddRelatedButton
 *
 * Sectioned add menu for attaching related media / records to a node.
 *
 * @param {object} node - Anchor node receiving related items.
 * @param {boolean} [iconOnly=false] - Render as a compact icon button.
 * @param {boolean} [asButton=false] - Render using shared Button (action-row style).
 * @param {string} [label='Add'] - Trigger label when not iconOnly.
 * @param {string} [iconKey='add'] - Trigger icon key.
 * @param {Function} [onQuickActionSelect] - Called for non-email quick actions.
 * @param {Function} [onSpecialAddSelect] - Reserved special-add callback.
 * @param {Function} [onExistingAttached] - Called when an existing item is attached.
 * @param {boolean} [showImport=true] - Show the Import… submenu (CSV / PO items).
 *
 * @returns {ReactNode} The rendered component.
 */
const NodeAddRelatedButton = React.memo(({
  node,
  iconOnly = false,
  asButton = false,
  label = 'Add',
  iconKey = 'add',
  onQuickActionSelect,
  onExistingAttached,
  showImport = true,
}) => {

  // ----- CONTEXT ----- //

  const { theme } = useTheme();

  // ----- REFS ----- //

  const triggerRef = useRef(null);

  // ----- HOOKS ----- //

  const {
    relatedTypeKeys,
    modals,
    handleTypeSelect,
    handleLinkExisting,
    handleImportTypeSelect,
    handleImportPurchaseOrderItems,
    showPurchaseOrderItemsImport,
  } = useNodeAddRelated({
    node,
    onQuickActionSelect,
    onExistingAttached,
  });

  const nodeTitle = String(node?.title || node?.name || '').trim();
  const tooltip = nodeTitle
    ? `Add photos and more to ${nodeTitle}`
    : 'Add photos and more';

  // ----- RENDER ----- //

  return (
    <>
      {asButton ? (
        <span ref={triggerRef} className="node-add-related-button-trigger">
          <Button
            backgroundColor="transparent"
            foregroundColor={theme.foregroundMuted}
            hoverForegroundColor={theme.foreground}
            enabled
            fontSize="12px"
            height="34px"
            iconKey={iconKey}
            label={label}
            padding="0 12px"
            title={tooltip}
          />
        </span>
      ) : iconOnly ? (
        <span ref={triggerRef} className="node-add-related-button-trigger">
          <Button
            iconKey={iconKey}
            iconSize="22px"
            foregroundColor={theme.foregroundMuted}
            hoverForegroundColor={theme.foreground}
            borderRadius="5px"
            title={tooltip}
            height="30px"
            width="30px"
            padding="0"
          />
        </span>
      ) : (
        <button
          ref={triggerRef}
          className="node-add-related-button"
          type="button"
          title={tooltip}
        >
          <Icon
            iconKey={iconKey}
            color={theme.foreground}
            width={18}
            height={18}
          />
          <span className="node-add-related-button-label">{label}</span>
        </button>
      )}

      <AddActionMenu
        mode="node"
        relatedTypeKeys={relatedTypeKeys}
        onImportTypeSelect={showImport ? handleImportTypeSelect : undefined}
        showPurchaseOrderItemsImport={showImport && showPurchaseOrderItemsImport}
        onImportPurchaseOrderItems={showImport ? handleImportPurchaseOrderItems : undefined}
        onLinkExisting={handleLinkExisting}
        onTypeSelect={handleTypeSelect}
        triggerRef={triggerRef}
      />

      {modals}
    </>
  );
});

NodeAddRelatedButton.displayName = 'NodeAddRelatedButton';

export default NodeAddRelatedButton;
